import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getChecklistByTemplate,
  createChecklistItem,
  deleteChecklistItem,
} from '../../services/checklistAPI';
import {
  setLoading,
  setError,
  setChecklistItems,
} from '../../redux/slices/checklistSlice';
import { Button, Modal, Input, Spin, message, Tooltip } from 'antd'; // Import Tooltip for info button
import { PlusOutlined, DeleteOutlined, InfoCircleOutlined, UploadOutlined } from '@ant-design/icons'; // Use Ant Design Icons
import { getTemplates } from '../../services/templateAPI';
import { useParams, useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';

const ChecklistManagementPage = () => {
  const dispatch = useDispatch();
  const { checklistItems, loading, error } = useSelector((state) => state.checklist);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null); // Use null for no template selected initially
  const [selectedTemplate, setSelectedTemplate] = useState(null); // Store the selected template object
  const [newQuestion, setNewQuestion] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [importLoading, setImportLoading] = useState(false);
  const [infoModalVisible, setInfoModalVisible] = useState(false); // State for the info modal


  const navigate = useNavigate();
  const { templateId: routeTemplateId } = useParams(); // Rename route param to avoid conflict
  const fileInputRef = useRef(null);

  // Fetch templates on component mount
  const fetchTemplates = async () => {
    dispatch(setLoading(true)); // Use slice action for loading
    try {
      const response = await getTemplates();
      setTemplates(response || []);
      // If a templateId is present in the route, try to select it
      if (routeTemplateId && response) {
        const foundTemplate = response.find((template) => template._id === routeTemplateId);
        if (foundTemplate) {
          setSelectedTemplateId(foundTemplate._id);
          setSelectedTemplate(foundTemplate);
        }
      }
      dispatch(setLoading(false)); // Use slice action for loading
    } catch (error) {
      dispatch(setError(error.message)); // Use slice action for error
      dispatch(setLoading(false)); // Use slice action for loading
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, [routeTemplateId]); // Depend on routeTemplateId to fetch if route changes

  // Fetch checklist items when a template is selected
  useEffect(() => {
    if (selectedTemplateId) {
      dispatch(setLoading(true)); // Use slice action for loading
      getChecklistByTemplate(selectedTemplateId)
        .then((data) => {
          dispatch(setChecklistItems(data)); // Use slice action for items
          dispatch(setLoading(false)); // Use slice action for loading
        })
        .catch((err) => {
          dispatch(setError(err.message)); // Use slice action for error
          dispatch(setLoading(false)); // Use slice action for loading
        });
    } else {
      dispatch(setChecklistItems([])); // Clear items if no template is selected
    }
  }, [selectedTemplateId, dispatch]); // Depend on selectedTemplateId

  // Handle template card click
  const handleTemplateSelect = (template) => {
    setSelectedTemplateId(template._id);
    setSelectedTemplate(template);
    navigate(`/admin/checklist/${template._id}`); // Update route on selection
  };

  // Handle adding a new question
  const handleAddQuestion = () => {
    if (newQuestion.trim() && selectedTemplateId) {
      createChecklistItem({ templateId: selectedTemplateId, question: newQuestion })
        .then(() => {
          message.success('Question added successfully');
          setModalVisible(false);
          setNewQuestion('');
          dispatch(setLoading(true));
          return getChecklistByTemplate(selectedTemplateId);
        })
        .then((data) => {
          dispatch(setChecklistItems(data));
          dispatch(setLoading(false));
        })
        .catch((err) => {
          message.error(`Error adding question: ${err.message}`);
          dispatch(setLoading(false)); // Ensure loading is unset on error
        });
    } else if (!selectedTemplateId) {
      message.warning('Please select a template first.');
    } else {
      message.warning('Please enter a question.');
    }
  };

  // Handle deleting a question
  const handleDeleteQuestion = (itemId) => {
    deleteChecklistItem(itemId)
      .then(() => {
        message.success('Question deleted');
        dispatch(setLoading(true));
        return getChecklistByTemplate(selectedTemplateId);
      })
      .then((data) => {
        dispatch(setChecklistItems(data));
        dispatch(setLoading(false));
      })
      .catch((err) => {
        message.error(`Error deleting question: ${err.message}`);
        dispatch(setLoading(false)); // Ensure loading is unset on error
      });
  };

  // Handle Excel file upload
  const handleExcelUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!selectedTemplateId) {
      message.warning('Please select a template first.');
      e.target.value = ''; // Reset file input
      return;
    }

    setImportLoading(true);

    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        const bstr = evt.target.result;
        const wb = XLSX.read(bstr, { type: 'binary' });
        const sheetName = wb.SheetNames[0];
        const ws = wb.Sheets[sheetName];

        const data = XLSX.utils.sheet_to_json(ws, { header: 1 });

        // Extract questions from the first column of each row
        let questions = [];
        for (let i = 0; i < data.length; i++) {
          const row = data[i];
          if (row && typeof row[0] === 'string' && row[0].trim().length > 0) {
            questions.push(row[0].trim());
          }
        }

        if (questions.length === 0) {
          message.warning('No valid questions found in the first column of the Excel file.');
          return;
        }

        // Create checklist items for each question
        for (const question of questions) {
          await createChecklistItem({ templateId: selectedTemplateId, question });
        }

        // Refresh the checklist items after import
        const updatedItems = await getChecklistByTemplate(selectedTemplateId);
        dispatch(setChecklistItems(updatedItems));
        message.success('Questions imported successfully');
      } catch (error) {
        console.error('Excel Import Error:', error);
        message.error('Failed to process Excel file. Please ensure it is a valid .xlsx or .xls file with questions in the first column.');
      } finally {
        setImportLoading(false);
        e.target.value = ''; // Reset file input for consecutive uploads
      }
    };

    reader.readAsBinaryString(file);
  };

  // Trigger hidden file input click
  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <div className="p-4 sm:p-6 min-h-screen"> {/* Adjusted padding */}
      <div className="max-w-6xl mx-auto"> {/* Increased max-width */}
        <div className="flex flex-col sm:flex-row justify-between items-center mb-6"> {/* Improved alignment */}
          <h2 className="text-2xl font-bold text-gray-800 mb-4 sm:mb-0">Checklist Management</h2> {/* Adjusted margin */}
          <Button onClick={() => navigate(`/admin/checklist/templates`)} className="w-full sm:w-auto">Manage Templates</Button> {/* Changed button text */}
        </div>

        {/* Template Selection Section */}
        <div className="mb-8 bg-gray-50 rounded-lg shadow-md p-6"> {/* Added padding and shadow */}
          <h3 className="text-xl font-semibold text-gray-700 mb-4">Select a Template</h3>
          {loading && templates.length === 0 ? (
             <div className="flex justify-center py-4"><Spin size="large" /></div>
          ) : templates.length === 0 ? (
            <p className="text-gray-600">No templates available. Please create a template first.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"> {/* Responsive grid for cards */}
              {templates.map((template) => (
                <div
                  key={template._id}
                  className={`cursor-pointer p-6 border rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 ${
                    selectedTemplateId === template._id ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-white'
                  }`}
                  onClick={() => handleTemplateSelect(template)}
                >
                  <h4 className="font-semibold text-gray-800">{template.title}</h4>
                  {/* Optional: Add template description or other details */}
                  {/* <p className="text-sm text-gray-600">{template.description}</p> */}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Checklist Items Section (Conditional Rendering) */}
        {selectedTemplateId && (
          <div className="bg-white rounded-lg shadow-md p-6"> {/* Added padding and shadow */}
            <h3 className="text-xl font-semibold text-gray-700 mb-4">{selectedTemplate?.title} Checklist Items</h3> {/* Display selected template title */}

            {/* Action Buttons */}
            <div className="mb-6 flex flex-wrap items-center gap-4"> {/* Adjusted spacing */}
              <Button
                type="primary"
                onClick={() => setModalVisible(true)}
                className="bg-blue-500 hover:bg-blue-700 text-white flex items-center" // Added flex and items-center
              >
                 <PlusOutlined className="mr-1" /> {/* Ant Design Icon */}
                Add Question
              </Button>

              <Button
                type="dashed"
                loading={importLoading}
                onClick={triggerFileInput}
                className="flex items-center" // Added flex and items-center
              >
                <UploadOutlined className="mr-1" /> {/* Ant Design Icon */}
                Upload from Excel
              </Button>

              {/* Info Button for Excel Upload */}
              <Tooltip title="Excel file should contain only the checklist questions in the first column (Column A), starting from the first row. No headers are required.">
                <Button
                  type="text" // Use text type for a less prominent icon button
                  icon={<InfoCircleOutlined className="text-gray-500 hover:text-gray-700" />} // Info icon
                  onClick={() => setInfoModalVisible(true)} // Optional: Use a modal for longer instructions
                  className="flex items-center justify-center" // Center icon
                />
              </Tooltip>

              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx, .xls"
                onChange={handleExcelUpload}
                style={{ display: 'none' }}
                disabled={importLoading}
              />
            </div>

            {/* Loading and Error States */}
            {loading && checklistItems.length === 0 ? ( // Check loading state for checklist items specifically
              <div className="flex justify-center py-4"><Spin size="large" /></div>
            ) : error ? (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                <strong className="font-bold">Error!</strong>
                <span className="block sm:inline"> {error}</span>
              </div>
            ) : checklistItems.length === 0 ? (
              <p className="text-gray-600">No questions added to this template yet.</p>
            ) : (
              /* Checklist Items List */
              <ul className="space-y-3">
                {checklistItems.map((item) => (
                  <li key={item._id} className="bg-gray-50 rounded-md shadow-sm p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between"> {/* Added flex-col/row for responsiveness */}
                    <p className="text-gray-800 mb-2 sm:mb-0 sm:mr-4 flex-grow">{item.question}</p> {/* Added margin and flex-grow */}
                    <Button
                      danger
                      icon={<DeleteOutlined />} // Ant Design Icon
                      onClick={() => handleDeleteQuestion(item._id)}
                      className="flex items-center" // Added flex and items-center
                    >
                      Delete
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Add New Question Modal */}
        <Modal
          title="Add New Question"
          open={modalVisible}
          onOk={handleAddQuestion}
          onCancel={() => setModalVisible(false)}
          footer={[
            <Button key="cancel" onClick={() => setModalVisible(false)}>
              Cancel
            </Button>,
            <Button
              key="add"
              type="primary"
              onClick={handleAddQuestion}
              className="bg-blue-500 hover:bg-blue-700 text-white"
              disabled={!newQuestion.trim()} // Disable if input is empty
            >
              Add
            </Button>,
          ]}
        >
          <Input
            value={newQuestion}
            onChange={(e) => setNewQuestion(e.target.value)}
            placeholder="Enter new question"
            onPressEnter={handleAddQuestion} // Allow adding with Enter key
          />
        </Modal>

        {/* Excel Upload Info Modal (Optional, using Tooltip for brevity) */}
         {/* Using Tooltip instead of a modal for a cleaner UI */}
         {/* <Modal
          title="Excel Upload Instructions"
          open={infoModalVisible}
          onCancel={() => setInfoModalVisible(false)}
          footer={[
            <Button key="close" onClick={() => setInfoModalVisible(false)}>
              Close
            </Button>,
          ]}
         >
          <p>Your Excel file should contain only the checklist questions in the first column (Column A).</p>
          <p>Start entering questions from the first row.</p>
          <p>No header row is required.</p>
         </Modal> */}

      </div>
    </div>
  );
};

export default ChecklistManagementPage;
